/**
 * Distinction régionale du néerlandais : Flandre (nl-BE) vs Pays-Bas (nl-NL).
 * Les JSON i18n restent sous `locales/nl/` (langue partagée) ; la région pilote
 * géo, SEO, labels UI et préférences stockées.
 *
 * @see docs/seo-international-pro/08-MARKET-HUBS-BE.md
 */

export const DUTCH_REGIONAL_CODES = ['nl-BE', 'nl-NL'] as const;
export type DutchRegionalCode = (typeof DUTCH_REGIONAL_CODES)[number];

export type DutchRegion = 'BE' | 'NL';

export const DUTCH_LOCALE_META: Record<
  DutchRegionalCode,
  {
    region: DutchRegion;
    /** Dossier / clé ressources i18next */
    resourceLang: 'nl';
    flag: string;
    name: string;
    /** Places / Nominatim */
    geoCountry: 'be' | 'nl';
    /** Préfixe SEO booking services (`/nl/...`) — uniquement nl-NL */
    seoRoutePrefix: '/nl' | null;
    ogLocale: 'nl_BE' | 'nl_NL';
    hreflang: 'nl-BE' | 'nl-NL';
    /** Hub marketing actuel */
    marketContentPath: string;
  }
> = {
  'nl-BE': {
    region: 'BE',
    resourceLang: 'nl',
    flag: '🇧🇪',
    name: 'Nederlands (België)',
    geoCountry: 'be',
    seoRoutePrefix: null,
    ogLocale: 'nl_BE',
    hreflang: 'nl-BE',
    marketContentPath: '/be/nl',
  },
  'nl-NL': {
    region: 'NL',
    resourceLang: 'nl',
    flag: '🇳🇱',
    name: 'Nederlands (Nederland)',
    geoCountry: 'nl',
    seoRoutePrefix: '/nl',
    ogLocale: 'nl_NL',
    hreflang: 'nl-NL',
    marketContentPath: '/nl',
  },
};

export function isDutchRegionalCode(code: string | null | undefined): code is DutchRegionalCode {
  return code === 'nl-BE' || code === 'nl-NL';
}

/** `nl` legacy et `nl-be` → Flandre ; `nl-nl` → Pays-Bas. */
export function normalizeDutchRegionalCode(
  raw: string | null | undefined
): DutchRegionalCode | null {
  if (!raw) return null;
  const v = raw.trim().replace('_', '-');
  const lower = v.toLowerCase();
  if (lower === 'nl-nl') return 'nl-NL';
  if (lower === 'nl-be' || lower === 'nl') return 'nl-BE';
  return null;
}

export function dutchResourceLanguage(code: string): 'nl' | null {
  if (isDutchRegionalCode(code) || code === 'nl') return 'nl';
  const regional = normalizeDutchRegionalCode(code);
  return regional ? 'nl' : null;
}
